import { FormField, fieldA11y } from '@/components/FormField';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { ProviderAccount } from '@/api/schemas';
import { useEditProviderForm, type EditProviderForm as Form } from './useEditProviderForm';

function EditHeader({ account }: { account: ProviderAccount }) {
  return (
    <SheetHeader>
      <SheetTitle>Edit {account.name}</SheetTitle>
      <SheetDescription id="provider-account-desc" className="flex flex-wrap items-center gap-1.5 text-xs">
        <Tag>{account.externalAccount ?? account.kind}</Tag>
        <span>the account name is fixed; replace the token from the row menu</span>
      </SheetDescription>
    </SheetHeader>
  );
}

function EditFields({ f, inUse }: { f: Form; inUse: number }) {
  const limitError = f.errorFor('concurrencyLimit', true);
  return (
    <>
      <FormField id="provider-account-edit-name" label="Name" required error={f.errorFor('name')}>
        <Input data-testid="provider-account-edit-name" value={f.name} onChange={(e) => f.setName(e.target.value)} maxLength={120} autoComplete="off" {...fieldA11y('provider-account-edit-name', f.errorFor('name'))} />
      </FormField>
      <FormField id="provider-account-edit-limit" label="Slots" required hint={`Concurrent builds; ${inUse} in use now. Lowering it never cancels a running build.`} error={limitError}>
        <Input data-testid="provider-account-edit-limit" type="number" min={1} max={100} value={f.concurrencyLimit} onChange={(e) => f.setConcurrencyLimit(e.target.value)} className="font-mono sm:max-w-32 sm:text-[13px]" {...fieldA11y('provider-account-edit-limit', limitError, 'hint')} />
      </FormField>
    </>
  );
}

/** The sheet's content when editing an account: its name and slots. */
export function EditForm({ account, onDone }: { account: ProviderAccount; onDone: () => void }) {
  const f = useEditProviderForm(account, onDone);
  return (
    <form data-testid="provider-account-edit-form" onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex h-full min-h-0 flex-col" aria-label={`Edit ${account.name}`}>
      <EditHeader account={account} />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
        <EditFields f={f} inUse={account.inUse} />
        <ProblemAlert error={f.problem} testId="provider-account-edit-problem" />
      </div>
      <SheetFooter className="flex-row justify-end border-t">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" data-testid="provider-account-edit-submit" loading={f.saving}>
          Save account
        </Button>
      </SheetFooter>
    </form>
  );
}
